import React, {useMemo} from 'react';
import {ActionIcon, Button, Checkbox, Divider, Group, Popover, Stack, Text, Title, Tooltip} from "@mantine/core";
import {IconArrowsSort, IconFilter, IconSortAscending, IconSortDescending} from "@tabler/icons-react";
import { deepCompareArrays } from '@repo/utils';
import AssetTypeIcon from "./AssetTypeIcon";
import {useGetAssetTypesQuery} from "@repo/services";

interface AssetTypeAndHealthFilterSortProps {
    sortOrders?: ('asc'|'desc'|'none')[];
    sortColumns?: string[];
    handleSort?: (column:string,order:'asc'|'desc'|'none') => void;
    assetTypeFilterOptions?: string[] | undefined;
    healthFilterOptions?: string[] | undefined;
    handleFilter?: (column:any, selectedValues:any) => void;
    selectedAssetTypeFilters?: string[];
    selectedHealthFilters?: string[];
}

const AssetTypeAndHealthFilterSort = ({
                                           assetTypeFilterOptions = undefined, handleFilter = () => {}, selectedAssetTypeFilters = [],
                                            healthFilterOptions = undefined,
                                           selectedHealthFilters, handleSort = () => {}, sortColumns = [], sortOrders = []
                                       }:AssetTypeAndHealthFilterSortProps) => {
    const { data: assetTypes = [] } = useGetAssetTypesQuery(null);
    const assetTypeIsFiltered = useMemo(() => {
        if (assetTypeFilterOptions) {
            const compareFilterData = deepCompareArrays(assetTypeFilterOptions, selectedAssetTypeFilters);
            if (!compareFilterData) return true;
            return false;
        }
    },[assetTypeFilterOptions, selectedAssetTypeFilters])

    const healthIsFiltered = useMemo(() => {
        if (healthFilterOptions) {
            const compareFilterData = deepCompareArrays(healthFilterOptions, selectedHealthFilters);
            if (!compareFilterData) return true;
            return false;
        }
    },[healthFilterOptions, selectedHealthFilters])

    const isSorted = useMemo(() => sortColumns.includes('aimAssetType') || sortColumns.includes('health'),[sortColumns])

    const handleAssetTypeFilterCheck = (val:string, isAdd:boolean) => {
        if (val) {
            const lowerCaseVal = val?.toLowerCase();
            const filters = selectedAssetTypeFilters ? [...selectedAssetTypeFilters as string []]?.map((filter) => filter?.toLowerCase()) : [] as string[] ;
            if (isAdd && filters) {
                filters.push(lowerCaseVal);
            } else {
                const index = filters?.indexOf(lowerCaseVal);
                if (index !== -1) filters?.splice(index, 1);
            }
            if (handleFilter) {
                handleFilter('aimAssetType', filters);
            }
        }
    }

    const handleHealthFilterCheck = (val:string, isAdd:boolean) => {
        if (val) {
            const lowerCaseVal = val?.toLowerCase();
            const filters = selectedHealthFilters ? [...selectedHealthFilters as string []]?.map((filter) => filter?.toLowerCase()) : [] as string[] ;
            if (isAdd && filters) {
                filters.push(lowerCaseVal);
            } else {
                const index = filters?.indexOf(lowerCaseVal);
                if (index !== -1) filters?.splice(index, 1);
            }
            if (handleFilter) {
                handleFilter('health', filters);
            }
        }
    }

    const sortAssetTypeOptionsByDescription = (options:string[]|undefined) => {
      if(!options) return [];
        return options.slice().sort((a, b) => {
            const aDescription = assetTypes.find(d => d.aimAssetType === a)?.description || a;
            const bDescription = assetTypes.find(d => d.aimAssetType === b)?.description || b;
            return aDescription.localeCompare(bDescription);
        });
    };

    const sortedAssetTypeFilterOptions = sortAssetTypeOptionsByDescription(assetTypeFilterOptions);

    const healthMap = {
        'red': 1,
        'yellow': 2,
        'green': 3
    } as  Record<string, number>;

    const sortHealthOptions = (options:string[]|undefined) => {
      if(!options) return [];
      return options.slice().sort((a, b) => {
            const aHealth = healthMap[a?.toLowerCase()] ?? Infinity;
            const bHealth = healthMap[b?.toLowerCase()] ?? Infinity;
            return aHealth - bHealth;
        }) || [];
    };

    const sortHealthFilterOptions = sortHealthOptions(healthFilterOptions).map((option) => {
        return {
            value: option || "",
            label: option || "",
        }
    });

    const SortButton = ({column, buttonText}:{column:string, buttonText:string}) => {
        const index = sortColumns?.indexOf(column);
        let sortOrder = 'none'
        if (sortOrders && index !== -1) {
            sortOrder = sortOrders[index] || 'none';
        }
        const isSortedByThisColumn = index !== -1;
        const nextOrderVal = useMemo(() => {
            let nextVal = 'none';
            if (sortOrder === 'none') {
                nextVal = 'asc'
            } else if (sortOrder === 'asc') {
                nextVal = 'desc'
            }
            return nextVal as 'asc' | 'desc' | 'none';
        },[sortOrder])
        return (
            <Button
                variant="default"
                rightSection={
                    <>
                        {sortOrder === 'none' && (
                            <Tooltip label='Sort Ascending'>
                                <IconArrowsSort size={16} />
                            </Tooltip>
                        )}
                        {sortOrder === 'asc' && (
                            <Tooltip label='Sort Descending'>
                                <IconSortDescending size={16} />
                            </Tooltip>
                        )}
                        {sortOrder === 'desc' && (
                            <Tooltip label='Sort Default'>
                                <IconSortAscending size={16} />
                            </Tooltip>
                        )}
                    </>
                }
                onClick={() => handleSort ? handleSort(column, nextOrderVal) : {}}
            >
                {buttonText}
            </Button>
        );
    }

    return (
        <>
            <Popover
                position="bottom-start"
                withArrow
            >
                <Popover.Target>
                    <ActionIcon
                        variant='default'
                        size='15'
                        aria-label='Gallery'
                        style={{border:'none'}}
                    >
                        <IconFilter color={assetTypeFilterOptions || healthIsFiltered || isSorted ? 'blue' : 'black'} />
                    </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown p={20}>
                    <Stack>
                        <Stack>
                            <Title order={5}>Sort</Title>
                            <Button.Group>
                                <SortButton column={'aimAssetType'} buttonText={"AssetType"} />
                                <SortButton column={'health'} buttonText={"Health"} />
                            </Button.Group>
                        </Stack>
                        <Divider my="md" />
                        <Stack>
                            <Title order={5}>AssetType Filter</Title>
                            {sortedAssetTypeFilterOptions?.map((option:string) => {
                                if (option && selectedAssetTypeFilters) {
                                    const lowerCaseFilters = (selectedAssetTypeFilters as string[])?.map((filter) => filter?.toLowerCase());
                                    const check = (lowerCaseFilters).includes(option?.toLowerCase());
                                    const assetType = assetTypes?.find((d) => d.aimAssetType === option) || null;
                                    return (
                                        <Group key={option} >
                                            <Checkbox
                                                checked={check}
                                                onChange={(event) => handleAssetTypeFilterCheck(option, event.currentTarget.checked)}
                                            />
                                            <AssetTypeIcon
                                                style={{ verticalAlign: 'middle', fontSize: 38 }}
                                                type={option}
                                            />
                                            <Text>{assetType?.description || option}</Text>
                                        </Group>
                                    )
                                }
                            })}
                        </Stack>
                        <Divider my="md" />
                        <Stack>
                            <Title order={5}>Health Filter</Title>
                            {sortHealthFilterOptions ? (sortHealthFilterOptions).map((option: any) => {
                                // console.log(sortHealthFilterOptions);
                                if (option && selectedHealthFilters) {
                                    const lowerCaseFilters = (selectedHealthFilters as string[])?.map((filter: string) => filter?.toLowerCase());
                                    const check = (lowerCaseFilters).includes(option.value?.toLowerCase());
                                    return (
                                        <Checkbox
                                          key={option.value}
                                            checked={check}
                                            label={option.label}
                                            onChange={(event) => handleHealthFilterCheck(option.value, event.currentTarget.checked)}
                                        />
                                    )
                                }
                            }) : undefined}
                        </Stack>
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        </>
    );
};

export default AssetTypeAndHealthFilterSort;
